import React, { createContext, useContext, useState, ReactNode } from 'react'

export interface TourStep {
  id: string
  target: string
  content: ReactNode
  placement?: 'top' | 'bottom' | 'left' | 'right'
}

export const TourLocalStorageKeys = {
  TOUR_DISMISSED: 'tourDismissed',
}

interface TourContextType {
  steps: TourStep[]
  registerStep: (step: TourStep) => void
  unregisterStep: (stepId: string) => void
}

const TourContext = createContext<TourContextType | undefined>(undefined)

export const TourProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [steps, setSteps] = useState<TourStep[]>([])

  const registerStep = React.useCallback((step: TourStep) => {
    setSteps((prev) => (prev.find((s) => s.id === step.id) ? prev : [...prev, step]))
  }, [])

  const unregisterStep = React.useCallback((stepId: string) => {
    setSteps((prev) => prev.filter((s) => s.id !== stepId))
  }, [])

  return (
    <TourContext.Provider value={{ steps, registerStep, unregisterStep }}>
      {children}
    </TourContext.Provider>
  )
}

export const useTourContext = (): TourContextType => {
  const context = useContext(TourContext)
  if (!context) {
    throw new Error('useTourContext must be used within a TourProvider')
  }
  return context
}
